import React, { useState, useCallback, useEffect } from 'react';
import { propOr, isEmpty, pathOr } from 'ramda';
import { useGoogleLogin } from '@react-oauth/google';
import {
  Page,
  Card,
  AppProvider,
  Layout,
  Frame,
  Button,
  Stack,
  Toast,
  SettingToggle,
  Modal,
  Form,
  FormLayout,
  TextField,
  Checkbox,
  Link,
} from '@shopify/polaris';
import { TitleBar } from '@shopify/app-bridge-react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import Footer from './Footer';
import { refreshTokenSetup } from '../../utils/googleToken';
import { CREATE_SHOP_EVENT, UPDATE_GOOGLE_INTEGRATION } from '../apollo/mutation';
import { GET_SETTINGS_INFO } from '../apollo/queries';

const Badge = ({ children }) => (
  <div
    style={{
      backgroundColor: '#bbe5b3',
      padding: '8px 20px',
      color: '#414f3e',
      borderRadius: '4px',
      border: '0.5px solid #8aa685',
    }}>
    {children}
  </div>
);
const GoogleSettings = ({ clientId, primaryDomain, shopifyToken, history }) => {
  const [toastStatus, setToastStatus] = useState({ status: false, message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [authObj, setAuthObj] = useState();
  const [googleIndexIntegration, setGoogleIndexIntegration] = useState(false);
  const [googleIntegrationEmail, setGoogleIntegrationEmail] = useState('');
  const [googleIntegrationJsonKey, setGoogleIntegrationJsonKey] = useState('');
  const [googleIntegrationModal, setGoogleIntegrationModal] = useState(false);

  const [createShopEvent] = useMutation(CREATE_SHOP_EVENT);
  const [updateGoogleIntegration] = useMutation(UPDATE_GOOGLE_INTEGRATION);

  const { loading, data } = useQuery(GET_SETTINGS_INFO, {
    variables: { shopifyToken },
  });
  const shop = propOr({}, 'shop')(data);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const isSitemapSubmitted = propOr(false, 'sitemapSubmitted')(shop);
  const isStarterPlan = isEmpty(currentSubscription);
  const contentGoogleIndexIntegration = googleIndexIntegration ? 'Disable' : 'Enable';
  const handleModalGoogleIntegration = useCallback(() => setGoogleIntegrationModal(!googleIntegrationModal));

  const [form, setState] = useState({
    googleIntegrationJsonKey: '',
    googleIntegrationEmail: '',
    googleIntegrationActive: true,
  });

  const updateField = (name) => (value) => {
    setState({
      ...form,
      [name]: value,
    });
  };

  useEffect(() => {
    if (shop.googleIndexIntegration) {
      setState({
        googleIntegrationJsonKey: shop.googleIndexIntegration.token,
        googleIntegrationEmail: shop.googleIndexIntegration.email,
        googleIntegrationActive: shop.googleIndexIntegration.isActive,
      });
    }
    setSubmitted(isSitemapSubmitted);
  }, [shop]);

  const onAuthSuccess = (res) => {
    setAuthenticated(true);
    setAuthObj(res);
    refreshTokenSetup(res);
    createShopEvent({ variables: { shopifyToken, event: 'google_authentication_success' } });
  };

  const onAuthFailure = (res) => {
    alert(`Failed to authenticate to Google services because of error: ${res.error}`);
    setAuthenticated(false);
    setAuthObj();
  };

  const signIn = useGoogleLogin({
    flow: 'implicit',
    onSuccess: onAuthSuccess,
    onError: onAuthFailure,
    onNonOAuthError: (err) => { alert(err); },
    clientId,
    scope: 'https://www.googleapis.com/auth/webmasters',
    hint: propOr({}, 'profile')(shop).contactEmail
  });

  const submit = () => {
    const token = authObj.access_token;
    fetch(
      `https://www.googleapis.com/webmasters/v3/sites/${encodeURIComponent(
        `https://${primaryDomain}`
      )}/sitemaps/${encodeURIComponent(`https://${primaryDomain}/a/careers/sitemap_careers.xml`)}`,
      {
        method: 'PUT',
        headers: {
          'Content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
      }
    )
      .then((data) => {
        const { error } = { ...data };
        if (!!error) {
          const { message } = { ...error };
          console.log(error);
          alert(message);
          setSubmitted(false);
        } else {
          setSubmitted(true);
          createShopEvent({ variables: { shopifyToken, event: 'google_sitemap_submitted' } });
        }
      })
      .catch((err) => {
        setSubmitted(false);
        alert(err);
      });
  };

  const addGoogleIntegration = async () => {
    const response = await updateGoogleIntegration({
      variables: {
        shopifyToken: shopifyToken,
        token: form.googleIntegrationJsonKey,
        email: form.googleIntegrationEmail,
        isActive: form.googleIntegrationActive,
      },
    });
    setToastStatus({ status: true, message: 'Settings successfully updated' });
    handleModalGoogleIntegration();
    setGoogleIntegrationEmail(form.googleIntegrationEmail);
    setGoogleIntegrationJsonKey(form.googleIntegrationJsonKey);
    const errors = pathOr([], ['data', 'updateGoogleIntegration', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const modalEditGoogleIntegration = (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
      }}>
      <Link url={'https://help.shophire.co/article/43-how-to-set-up-google-for-jobs-indexing-api'} external removeUnderline>
        How to set up Google for Jobs Indexing API?
      </Link>
      <br />
      <br />
      <FormLayout>
        <TextField
          label='Google Service Account email'
          value={form.googleIntegrationEmail}
          name='googleIntegrationEmail'
          onChange={updateField('googleIntegrationEmail')}
        />
        <TextField
          label='JSON Key'
          value={form.googleIntegrationJsonKey}
          name='googleIntegrationJsonKey'
          multiline={5}
          onChange={updateField('googleIntegrationJsonKey')}
        />
        <Checkbox
          label='Enable Google Indexing AP'
          checked={form.googleIntegrationActive}
          onChange={updateField('googleIntegrationActive')}
        />
      </FormLayout>
    </Form>
  );

  if (!loading) {
    return (
        <AppProvider>
          <Frame>
            <TitleBar title='Google for Jobs' breadcrumbs={[{ content: 'Settings', onAction: () => { history.push('/settings'); } }]} />
            <Page title='Google for Jobs' breadcrumbs={[{ content: 'Settings', onAction: () => { history.push('/settings'); } }]}>
              <Layout>
                {toastStatus.status && (
                  <Toast content={toastStatus.message} onDismiss={() => setToastStatus({ status: false, message: '' })} />
                )}
                <Layout.AnnotatedSection
                  title='Google for Jobs Settings'
                  description={
                    <p style={{ marginBottom: '10px' }}>
                      Submitting your career portal sitemap to Google Search Console let’s ShopHire monitor your jobs’ search
                      performance.
                    </p>
                  }>
                  <Card>
                    <p style={{
                        paddingLeft: '15px',
                        paddingRight: '15px',
                        paddingTop: '15px'
                      }}>
                      Before you begin make sure you have account owner privileges for your store's
                      <Link url={`https://search.google.com/search-console/about`} external={true} removeUnderline> Google Search console</Link>
                      and have <Link url={`https://help.shopify.com/en/manual/online-store/os/domains`} external={true} removeUnderline> configured your custom domain  in Shopify</Link>.
                    </p>
                    <Card.Section title={'1) Authenticate with Google'}>
                      <br />
                      <Stack>
                        <Stack.Item fill>
                          ShopHire is <b>{authenticated ? '' : 'not'} connected</b> to Google
                        </Stack.Item>
                        {!authenticated ? (
                          <Button disabled={authenticated} primary onClick={signIn}>
                            {'Sign in with Google'}
                          </Button>
                        ) : (
                          <Badge>Authenticated</Badge>
                        )}
                      </Stack>
                    </Card.Section>
                    <Card.Section title={'2) Submit career portal sitemap to Google'}>
                      <br />
                      <Stack>
                        <Stack.Item fill>
                          Career portal sitemap {submitted ? 'has been ' : ' '}
                          <b>{!submitted ? 'has not been' : ''} submitted</b> to Google
                        </Stack.Item>
                        {!submitted ? (
                          <Button primary disabled={!authenticated || submitted} onClick={submit}>
                            {'Submit sitemap'}
                          </Button>
                        ) : (
                          <Badge>Submited</Badge>
                        )}
                      </Stack>
                    </Card.Section>
                  </Card>
                </Layout.AnnotatedSection>
                <Layout.AnnotatedSection
                  title='Google for Jobs Indexing API'
                  description={
                    <>
                      <p>Configure indexing API to notify google of new job postings instantly.</p>

                      {isStarterPlan && (
                          <Link onClick={() => history.push(`/account-plan`)} removeUnderline>
                          Upgrade account plan
                        </Link>
                      )}
                    </>
                  }>
                  <SettingToggle
                    action={{
                      content: 'Edit',
                      onAction: handleModalGoogleIntegration,
                      disabled: isStarterPlan,
                      primary: true,
                    }}
                    enabled={contentGoogleIndexIntegration}>
                    <p>
                      <br />
                      Use indexing API to notify google of new job postings is <b>{!isStarterPlan ? 'enabled' : 'disabled'}</b>
                    </p>
                    <br />
                  </SettingToggle>
                </Layout.AnnotatedSection>
              </Layout>
            </Page>
            <Footer link='https://help.shophire.co/article/43-how-to-set-up-google-for-jobs-indexing-api' name='Google for Jobs' />
          </Frame>
          <Modal
            open={googleIntegrationModal}
            onClose={handleModalGoogleIntegration}
            title='Add Google Service Account and API Key'
            primaryAction={{
              content: 'Save',
              disabled: !form.googleIntegrationJsonKey || !form.googleIntegrationEmail,
              onAction: () => addGoogleIntegration(),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: () => handleModalGoogleIntegration(),
              },
            ]}
            loading={false}>
            <Modal.Section>{modalEditGoogleIntegration}</Modal.Section>
          </Modal>
        </AppProvider>
    );
  } else {
    return (
      <AppProvider>
        <div
          style={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
        </div>
      </AppProvider>
    );
  }
};

export default GoogleSettings;
